/**
 * GenUI bento document — LLM-composed cells + col spans; FE owns grid + widgets.
 * Callers: parse-genui, InsightsGenuiBento, fixtures, host answer gate (mirrored).
 */

export const GENUI_WIDGET_KINDS = [
  "header",
  "kpi",
  "table",
  "bar",
  "actions",
  "estate",
  "caveat",
  "meta",
  "text",
] as const;

export type GenuiWidgetKind = (typeof GENUI_WIDGET_KINDS)[number];

export const GENUI_COL_SPANS = [3, 4, 6, 8, 12] as const;
export type GenuiColSpan = (typeof GENUI_COL_SPANS)[number];

export type GenuiHeaderPayload = {
  title: string;
  subtitle?: string;
  frame?: string;
};

export type GenuiKpiPayload = {
  label: string;
  value: string;
  hint?: string;
};

export type GenuiActionsPayload = {
  items: string[];
};

export type GenuiEstateItem = {
  id: string;
  label: string;
  status: string;
};

export type GenuiEstatePayload = {
  items: GenuiEstateItem[];
};

export type GenuiCaveatPayload = {
  text: string;
};

export type GenuiMetaPayload = {
  asOf?: string;
  investigationId?: string;
  frame?: string;
};

export type GenuiTextPayload = {
  markdown: string;
};

export type GenuiTablePayload = {
  title?: string;
  columns: string[];
  rows: Array<Array<string | number | null | undefined>>;
};

export type GenuiBarPayload = {
  title?: string;
  x: string;
  y: string;
  data: Array<{ label: string; value: number }>;
};

export type GenuiCellPayload =
  | GenuiHeaderPayload
  | GenuiKpiPayload
  | GenuiActionsPayload
  | GenuiEstatePayload
  | GenuiCaveatPayload
  | GenuiMetaPayload
  | GenuiTextPayload
  | GenuiTablePayload
  | GenuiBarPayload;

export type InsightsGenuiBentoCell = {
  id: string;
  kind: GenuiWidgetKind;
  colSpan: GenuiColSpan;
  /** Optional key from WORKFLOW_ICON_KEYS (same catalog as flow-builder plan steps). */
  icon?: string;
  payload: GenuiCellPayload;
};

export type InsightsGenuiBento = {
  type: "bento";
  cols: 12;
  cells: InsightsGenuiBentoCell[];
};

export function isGenuiWidgetKind(value: unknown): value is GenuiWidgetKind {
  return typeof value === "string" && (GENUI_WIDGET_KINDS as readonly string[]).includes(value);
}

export function isGenuiColSpan(value: unknown): value is GenuiColSpan {
  return typeof value === "number" && (GENUI_COL_SPANS as readonly number[]).includes(value);
}

/** Tailwind grid span classes — mobile stack, sm–lg cap at 6 (except 12), lg exact. */
export function genuiColSpanClass(colSpan: GenuiColSpan): string {
  switch (colSpan) {
    case 3:
      return "col-span-12 sm:col-span-3 lg:col-span-3";
    case 4:
      return "col-span-12 sm:col-span-4 lg:col-span-4";
    case 6:
      return "col-span-12 sm:col-span-6 lg:col-span-6";
    case 8:
      return "col-span-12 sm:col-span-6 lg:col-span-8";
    case 12:
      return "col-span-12";
    default:
      return "col-span-12";
  }
}
